import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PaginationControls from '../PaginationControls';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    Stack,
    Card,
    CardContent,
    Snackbar,
    LinearProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Phone as PhoneIcon,
    PhoneDisabled as MissedCallIcon,
    CallMade as OutgoingIcon,
    CallReceived as IncomingIcon,
    Videocam as MeetingIcon,
    VerifiedUser as TrustCheckIcon,
    Close as CloseIcon,
    Event as EventIcon,
    AccessTime as AccessTimeIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Check as CheckIcon,
    Pending as PendingIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Fingerprint as FingerprintIcon
} from '@mui/icons-material';
import useLocalStorage from '../../hooks/useLocalStorage';
import useUrlFilters from '../../hooks/useUrlFilters';
import useTableSettings from '../../hooks/useTableSettings';
import { usePolyAuth } from '../../hooks/PolyAuthContext';
import { toast } from 'sonner';
import useSWR from 'swr';
import TableSkeleton from '../TableSkeleton';
import {
    getLinkTypeLabel,
    getPresenceScoreColor,
    formatBiometricConfidence,
    normalizeTrustCheckEntry,
} from '../trustCheckLogs';
import {
    PLATFORM_LOGO_URL,
    formatTimestamp,
    getTypeIcon,
    getMeetingTypeLabel,
    getVerificationStatusChip,
    getOverallVerificationStatus,
    getLogTypeTitle,
} from './helpers';
import {
    NoCertificateTag,
    PresenceScoreChip,
    RowTypeIndicator,
    TrustCheckKindBadge,
} from './RowIndicators';
import { DownloadAffidavitButton } from './DownloadAffidavitButton';
import { FilterPanel } from './FilterPanel';
import { MeetingDetailsModal } from './MeetingDetailsModal';
import { useCallLogsData } from './useCallLogsData';


const CallLogs = ({
    basePath = '/ms-teams/log',
    openURL = (url) => window.open(url, '_blank', 'noopener,noreferrer'),
    defaultViewMode = 'user',
    appId = null,
    // false for a single app's logs: the account view is the only one that
    // makes sense, and an app is either meetings or trust checks, never both.
    showViewFilters = true,
    // Optional empty-state slot. Rendered in place of the table when the
    // account has zero logs AND there is no active filter — i.e. the
    // "you haven't run any verifications yet" case, not the "your filter
    // matched nothing" case. Existing callers (Zoom app) don't pass this
    // prop and continue to see the inline text fallback.
    emptyState = null,
}) => {
    const { t, i18n } = useTranslation('shared');

    const [sessionToken] = useLocalStorage('sessionToken');

    const { accountId: polyAccountId, accountRoles, api_client, login } = usePolyAuth();
    const adminRoles = ['OWNER', 'ADMIN', 'POLYADMIN'];
    const isAdmin = polyAccountId && adminRoles.includes(accountRoles[polyAccountId]?.role);

    // Router hooks
    const navigate = useNavigate();
    const location = useLocation();
    const { logId } = useParams();
    
    const { settings: tableSettings, updateSetting } = useTableSettings('callLogs', {
        view: defaultViewMode,
        pageSize: 10,
    });
    
    const { filters, setFilter, setFilters } = useUrlFilters({
        view: tableSettings.view,
        pageSize: tableSettings.pageSize,
        page: 1,
    });
    
    // View mode with permission check (non-admins can't view account logs)
    const requestedView = showViewFilters ? filters.view : 'account';
    const viewMode = requestedView === 'account' && isAdmin ? 'account' :
                     requestedView === 'account' ? 'user' : requestedView;
    const setViewMode = useCallback((value) => {
        updateSetting('view', value);
        setFilters({ view: value, page: 1 });
    }, [updateSetting, setFilters]);
    const showUsers = viewMode === 'account';
    
    // Page and rows per page
    const page = filters.page;
    const setPage = useCallback((value) => setFilter('page', value), [setFilter]);
    const rowsPerPage = filters.pageSize;
    const setRowsPerPage = useCallback((value) => {
        updateSetting('pageSize', value);
        setFilters({ pageSize: value, page: 1 });
    }, [updateSetting, setFilters]);

    // Theme and responsive
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedLog, setSelectedLog] = useState(null);
    // Which sources to show. Both by default; FilterPanel keeps at least one.
    const [logTypes, setLogTypes] = useState(['meeting', 'trust_check']);

    // Opt-in mock mode: ?mock=1 swaps the live fetcher for fixture data so the
    // page can be exercised against the spec contract before the backend lands.
    // Strictly off by default — production code paths are unchanged.
    const mockMode = new URLSearchParams(location.search).get('mock') === '1';

    const {
        logs,
        totalItems,
        totalPages,
        loading,
        isValidating,
        error,
        mutate,
        accountLinkRequired,
        setAccountLinkRequired,
    } = useCallLogsData({
        api_client,
        polyAccountId,
        page,
        rowsPerPage,
        viewMode,
        isAdmin,
        debouncedSearchTerm,
        logTypes,
        mockMode,
        appId,
    });

    // Effect to find and set selected log when logId changes
    useEffect(() => {
        if (logId && logs?.length > 0) {
            const foundLog = logs.find(log => (log.log_id) === logId);
            if (foundLog) {
                setSelectedLog(foundLog);
            } else {
                navigate(basePath);
            }
        } else if (!logId) {
            setSelectedLog(null);
        }
    }, [logId, logs, navigate]);

    // When session token changes (e.g. login or account linking from another flow),
    // clear the account-link banner and re-fetch logs.
    useEffect(() => {
        if (accountLinkRequired && sessionToken) {
            setAccountLinkRequired(false);
            mutate();
        }
    }, [sessionToken]);


    // Handle opening and closing the details modal
    const handleOpenDetails = async (log) => {
        // Navigate to the log details URL, preserving search params
        navigate(`${basePath}/log/${log.log_id}${location.search}`);
    };

    // Debounce search term and reset page when search changes
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            if (searchTerm !== debouncedSearchTerm) {
                setPage(1);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);


    const handleLoginClick = async () => {
        try {
            await login();
            setAccountLinkRequired(false);
            mutate();
        } catch (err) {
            console.error('Login failed:', err);
        }
    };

    return (
        <Box sx={{ p: 4, backgroundColor: 'background.paper', position: 'relative', zIndex: 0 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }} color="text.primary">
                    {t('callLogs.title')}
                </Typography>
                {!isMobile && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FilterPanel
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            isAdmin={isAdmin}
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            logTypes={logTypes}
                            setLogTypes={(value) => { setLogTypes(value); setPage(1); }}
                            showViewSelect={showViewFilters}
                            showTypeFilters={showViewFilters}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => mutate()}
                            disabled={loading}
                            loading={loading}
                            sx={{ borderRadius: '8px' }}
                        >
                            {t('callLogs.refresh')}
                        </Button>
                    </Box>
                )}
            </Box>

            {accountLinkRequired && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('callLogs.linkAccountMessage')}
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleLoginClick}
                        sx={{ ml: 2 }}
                    >
                        {t('callLogs.login')}
                    </Button>
                </Alert>
            )}

            {!accountLinkRequired && <LinearProgress sx={{ visibility: isValidating && !loading ? 'visible' : 'hidden' }} />}
            {!accountLinkRequired && <TableContainer sx={{ mb: 1, position: 'relative', maxWidth: '100%', width: '100%' }}>
                {loading && logs?.length === 0 ? (
                    <TableSkeleton
                        columns={isMobile ? 2 : (showUsers ? 5 : 4)}
                        rows={rowsPerPage}
                        headers={isMobile
                            ? [t('callLogs.headerWhen'), t('callLogs.headerTitle')]
                            : showUsers
                                ? ['', t('callLogs.headerWhen'), t('callLogs.headerTitle'), t('callLogs.headerUser'), t('callLogs.headerAffidavit')]
                                : ['', t('callLogs.headerWhen'), t('callLogs.headerTitle'), t('callLogs.headerAffidavit')]
                        }
                    />
                ) : !loading && logs?.length === 0 && !debouncedSearchTerm && emptyState ? (
                    emptyState
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {!isMobile && <TableCell sx={{ width: 40 }} />}
                                <TableCell>{t('callLogs.headerWhen')}</TableCell>
                                <TableCell>{t('callLogs.headerTitle')}</TableCell>
                                {!isMobile && showUsers && <TableCell>{t('callLogs.headerUser')}</TableCell>}
                                {!isMobile && <TableCell>{t('callLogs.headerAffidavit')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs?.length > 0 ? (
                                logs.map((log, index) => {
                                    const formattedTime = formatTimestamp(log.timestamp, i18n.language);
                                    const isTrustCheck = log.callType === 'trust_check';

                                    // Ensure each row has a truly unique key to prevent React from reusing components
                                    const uniqueKey = log.log_id ? `${log.log_id}-${index}` : `log-${index}-${Math.random().toString(36).substr(2, 9)}`;

                                    return (
                                        <TableRow
                                            key={uniqueKey}
                                            hover
                                            onClick={() => handleOpenDetails(log)}
                                            data-testid={isTrustCheck ? `row-trust-check-${log.trust_check_session_id}` : `row-meeting-${log.meeting_id}`}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            {!isMobile && (
                                                <TableCell sx={{ width: 40 }}>
                                                    <RowTypeIndicator log={log} t={t} />
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2" component="span">
                                                    {formattedTime.date}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    color="text.secondary"
                                                    sx={{ ml: 1 }}
                                                >
                                                    {formattedTime.time}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isMobile && <RowTypeIndicator log={log} t={t} />}
                                                    <Typography variant="body2">
                                                        {log.title || t('callLogs.untitled')}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {!isMobile && showUsers && (
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {isTrustCheck
                                                            ? (log.verified_subject_name || t('callLogs.unknown'))
                                                            : (log.user_name || t('callLogs.unknown'))}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            {!isMobile && (
                                                <TableCell>
                                                    {isTrustCheck ? (
                                                        log.affidavit_key ? (
                                                            <DownloadAffidavitButton
                                                                appId={log.app_id}
                                                                trustCheckSessionId={log.trust_check_session_id}
                                                                api_client={api_client}
                                                                openURL={openURL}
                                                                t={t}
                                                            />
                                                        ) : (
                                                            <NoCertificateTag t={t} />
                                                        )
                                                    ) : (
                                                        // Meeting rows: the existing modal "Generate Report" button
                                                        // already covers PDF generation; surface a tooltip here
                                                        // pointing the user there.
                                                        <Tooltip title={t('callLogs.openMeetingForReport')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenDetails(log);
                                                                }}
                                                                aria-label={t('callLogs.openMeetingForReport')}
                                                            >
                                                                <DescriptionIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={isMobile ? 2 : (showUsers ? 5 : 4)} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            {logs?.length > 0
                                                ? t('callLogs.noLogsMatchingFilters')
                                                : loading
                                                    ? t('callLogs.loadingLogs')
                                                    : t('callLogs.noLogsFound')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>}

            {!accountLinkRequired && <PaginationControls
                page={page}
                totalPages={totalPages}
                pageSize={rowsPerPage}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(val) => setRowsPerPage(Number(val))}
                pageSizeOptions={[5, 10, 25, 50]}
                itemLabel={t('callLogs.itemLabel')}
                loading={loading}
                searchTerm={debouncedSearchTerm}
                currentPageCount={logs?.length}
            />}

            <MeetingDetailsModal selectedLog={selectedLog} onClose={() => navigate(`${basePath}${location.search}`)} openURL={openURL} />

        </Box>
    );
};




export default CallLogs;
